import React, { useEffect, useMemo, useRef, useState } from 'react';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';
import { TimeAgo } from '@mysimpledb/shared/components/UI/TimeAgo';
import {
  handleNextResult,
  handlePrevResult,
} from '@mysimpledb/shared/hooks/usePaginationHelpers';
import {
  DEFAULT_NOTE_CATEGORY_COLOR,
  getNoteCategoryColor,
} from '@mysimpledb/shared/constants/noteCategoryColors';
import './MyNotesWeb.css';


const RICH_TEXT_ALLOWED_TAGS = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'STRIKE',
  'BR',
  'P',
  'DIV',
  'UL',
  'OL',
  'LI',
]);

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function sanitizeRichHtml(value) {
  if (!value) return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return escapeHtml(value);
  }

  const doc = new DOMParser().parseFromString(`<div>${value}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';

  const cleanNode = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
        return;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) return;

      if (!RICH_TEXT_ALLOWED_TAGS.has(child.tagName)) {
        child.replaceWith(...Array.from(child.childNodes));
        return;
      }

      Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name));
      cleanNode(child);
    });
  };

  cleanNode(root);
  return root.innerHTML;
}

function normalizeTitleHtml(value) {
  let html = sanitizeRichHtml(value || '');

  // contentEditable may represent Enter as <div>, <p>, or <br> depending
  // on the browser/caret position. Titles use one canonical representation:
  // inline formatting plus <br> for every manual line break.
  html = html
    .replace(/<p>/gi, '')
    .replace(/<\/p>/gi, '<br>')
    .replace(/<div>/gi, '')
    .replace(/<\/div>/gi, '<br>')
    .replace(/<br\s*\/?>/gi, '<br>')
    .replace(/(?:<br>){3,}/g, '<br><br>')
    // Remove accidental blank lines before the title, but preserve trailing
    // <br>. A trailing <br> is how contentEditable keeps the caret on a new
    // blank line after Enter at the end of the title.
    .replace(/^(?:<br>)+/g, '');

  return html;
}

function htmlToPlainText(html) {
  if (!html) return '';
  if (typeof document === 'undefined') return String(html).replace(/<[^>]*>/g, '');
  const div = document.createElement('div');
  div.innerHTML = sanitizeRichHtml(html);
  return (div.innerText || div.textContent || '').replace(/\u00a0/g, ' ');
}

const NOTE_TITLE_MAX_LENGTH = 100;

function RichTextEditor({
  value,
  onChange,
  titleMode = false,
  ariaLabel,
  maxLength = null,
  onLimitReached,
}) {
  const editorRef = useRef(null);
  const lastValidHtmlRef = useRef('');

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const safeValue = titleMode
      ? normalizeTitleHtml(value || '')
      : sanitizeRichHtml(value || '');

    const currentSafeValue = titleMode
      ? normalizeTitleHtml(editor.innerHTML)
      : sanitizeRichHtml(editor.innerHTML);

    const isFocused =
      typeof document !== 'undefined' && document.activeElement === editor;

    // Replacing innerHTML destroys the current Selection/Range. While the
    // editor is focused, do not rewrite it if its live DOM is already
    // equivalent to the React value.
    if (!(isFocused && currentSafeValue === safeValue)) {
      if (editor.innerHTML !== safeValue) {
        editor.innerHTML = safeValue;
      }
    }

    if (!maxLength || htmlToPlainText(safeValue).length <= maxLength) {
      lastValidHtmlRef.current = safeValue;
    }
  }, [value, titleMode, maxLength]);

  const publishValue = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const safeHtml = titleMode
      ? normalizeTitleHtml(editor.innerHTML)
      : sanitizeRichHtml(editor.innerHTML);

    // Do not assign editor.innerHTML here. Doing so resets the caret/selection.
    // Keep the live DOM intact and publish the normalized value to React.
    const plainText = htmlToPlainText(safeHtml);
    if (maxLength && plainText.length > maxLength) {
      editor.innerHTML = lastValidHtmlRef.current;
      onLimitReached?.();
      return;
    }

    lastValidHtmlRef.current = safeHtml;
    onChange(safeHtml, plainText);
  };

  const runCommand = (command) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    document.execCommand(command, false, null);
    publishValue();
  };

  const handleKeyDown = (event) => {
    if (titleMode && event.key === 'Enter') {
      event.preventDefault();

      // Ask Chromium to insert a true contentEditable line break. This moves
      // the caret to the next line even when Enter is pressed at the very end.
      const inserted = document.execCommand('insertLineBreak', false);

      // Fallback for environments where insertLineBreak is unavailable.
      if (!inserted) {
        const selection = window.getSelection();
        if (selection?.rangeCount) {
          const range = selection.getRangeAt(0);
          range.deleteContents();

          const br = document.createElement('br');
          range.insertNode(br);
          range.setStartAfter(br);
          range.collapse(true);

          selection.removeAllRanges();
          selection.addRange(range);
        }
      }

      publishValue();
      return;
    }

    if ((event.metaKey || event.ctrlKey) && !event.shiftKey) {
      const key = event.key.toLowerCase();
      if (key === 'b') {
        event.preventDefault();
        runCommand('bold');
      } else if (key === 'i') {
        event.preventDefault();
        runCommand('italic');
      } else if (key === 'u') {
        event.preventDefault();
        runCommand('underline');
      }
    }
  };

  const handlePaste = (event) => {
    if (!titleMode) return;

    event.preventDefault();
    let text = event.clipboardData?.getData('text/plain') || '';
    if (maxLength) {
      const currentLength = htmlToPlainText(editorRef.current?.innerHTML || '').length;
      const selectedLength = window.getSelection()?.toString().length || 0;
      const availableLength = Math.max(0, maxLength - currentLength + selectedLength);
      if (text.length > availableLength) {
        onLimitReached?.();
      }
      text = text.slice(0, availableLength);
    }
    const safeHtml = escapeHtml(text)
      .replace(/\r\n?/g, '\n')
      .replace(/\n/g, '<br>');

    document.execCommand('insertHTML', false, safeHtml);
    publishValue();
  };

  const button = (label, command, title) => (
    <button
      type="button"
      className="notes-rich-toolbar-button"
      title={title}
      aria-label={title}
      onMouseDown={(event) => {
        event.preventDefault();
        runCommand(command);
      }}
    >
      {label}
    </button>
  );

  return (
    <div className={`notes-rich-editor-shell ${titleMode ? 'is-title-editor' : ''}`}>
      <div className="notes-rich-toolbar" role="toolbar" aria-label={`${ariaLabel} formatting`}>
        {button(<strong>B</strong>, 'bold', 'Bold')}
        {button(<em>I</em>, 'italic', 'Italic')}
        {button(<u>U</u>, 'underline', 'Underline')}
        {button(<s>S</s>, 'strikeThrough', 'Strikethrough')}
        {!titleMode && (
          <>
            <span className="notes-rich-toolbar-separator" aria-hidden="true" />
            {button('• List', 'insertUnorderedList', 'Bulleted list')}
            {button('1. List', 'insertOrderedList', 'Numbered list')}
          </>
        )}
      </div>

      <div
        ref={editorRef}
        className="notes-rich-editor"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        data-placeholder={titleMode ? 'Note title' : 'Add note details…'}
        onInput={publishValue}
        onBlur={publishValue}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
    </div>
  );
}

function getCategoryStyle(category) {
  const color = getNoteCategoryColor(
    category?.color_key ||
      category?.colorKey ||
      DEFAULT_NOTE_CATEGORY_COLOR
  );

  return {
    '--category-background': color.background,
    '--category-border': color.border,
    '--category-text': color.text,
  };
}

function isPlainLocalDateTimeString(value) {
  return (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)
  );
}

function parseDateValue(value) {
  if (!value) return null;

  if (isPlainLocalDateTimeString(value)) {
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second = 0] = timePart.split(':').map(Number);

    const d = new Date(year, month - 1, day, hour, minute, second, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatLocalDateTimeInput(value) {
  if (!value) return '';

  if (isPlainLocalDateTimeString(value)) {
    return value.slice(0, 16);
  }

  const d = parseDateValue(value);
  if (!d) return '';

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function formatDisplayDateTime(value) {
  if (!value) return '';

  const d = parseDateValue(value);
  if (!d) return '';

  return d.toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function toApiDateTime(value) {
  if (!value) return null;

  if (isPlainLocalDateTimeString(value)) {
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second = 0] = timePart.split(':').map(Number);

    const d = new Date(year, month - 1, day, hour, minute, second, 0);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function MyNotes({
  currentIndex,
  notes,
  notePageNo,
  fetchPage,
  focusNoteAfterSave,
  form,
  hasLoaded,
  isEditing,
  itemsPerPage,
  listPageNo,
  searchTerm,
  setActiveTab,
  setCurrentIndex,
  setForm,
  setIsEditing,
  setListPageNo,
  token,
  totalNotesFound,
  userId,
  fetchNoteById,
  saveNote,
  deleteNote,
  categories = [],
  selectedCategoryIds = [],
  uncategorizedOnly = false,
}) {
  const [isNew, setIsNew] = useState(false);
  const [lastNoteId, setLastNoteId] = useState(null);
  const [titleLimitMessage, setTitleLimitMessage] = useState('');

  const showNoNotesState =
    hasLoaded &&
    !isEditing &&
    !isNew &&
    notes.length === 0 &&
    totalNotesFound === 0;

  const initialTitleHtml =
    form.titleHtml && form.titleHtml.trim().length
      ? normalizeTitleHtml(form.titleHtml)
      : escapeHtml(form.title || '').replace(/\n/g, '<br>');

  const initialDetailHtml =
    form.detailHtml && form.detailHtml.trim().length
      ? sanitizeRichHtml(form.detailHtml)
      : form.detail
      ? `<p>${escapeHtml(form.detail).replace(/\n/g, '<br/>')}</p>`
      : '';

  const [titleEditorHtml, setTitleEditorHtml] = useState(initialTitleHtml);
  const [editorHtml, setEditorHtml] = useState(initialDetailHtml);

  useEffect(() => {
    if (!isEditing) return;
    setTitleEditorHtml(initialTitleHtml || '');
    setEditorHtml(initialDetailHtml || '');
  }, [isEditing, form?.id, initialTitleHtml, initialDetailHtml]);

  useEffect(() => {
    const id = notes[currentIndex]?.id;
    if (!id || !fetchNoteById) return;

    (async () => {
      const note = await fetchNoteById(id, token);
      if (!note) return;

      setForm({
        id: note.id,
        title: note.title || '',
        titleHtml: note.title_html || '',
        detail: note.detail || '',
        detailHtml: note.detail_html || '',
        dueAt: note.due_at || '',
        remindAt: note.remind_at || '',
        expiresAt: note.expires_at || '',
        sendSmsReminder: !!note.send_sms_reminder,
        displayMode: note.display_mode || 'normal',
        categories: Array.isArray(note.categories) ? note.categories : [],
        categoryIds: Array.isArray(note.categories)
          ? note.categories.map((category) => Number(category.id))
          : [],
        primaryCategoryId:
          note.primary_category_id != null
            ? Number(note.primary_category_id)
            : note.primaryCategoryId != null
              ? Number(note.primaryCategoryId)
              : Array.isArray(note.categories) && note.categories.length
                ? Number(note.categories[0].id)
                : null,
        updatedAt: note.updated_at || null,
        userId: note.user_id,
      });
    })();
  }, [currentIndex, notes, token, fetchNoteById, setForm]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const toggleCategory = (categoryId) => {
    const numericId = Number(categoryId);
    setForm((prev) => {
      const currentIds = Array.isArray(prev.categoryIds)
        ? prev.categoryIds.map(Number)
        : [];
      const selected = currentIds.includes(numericId);
      const nextIds = selected
        ? currentIds.filter((id) => id !== numericId)
        : [...currentIds, numericId];

      const currentPrimary =
        prev.primaryCategoryId != null ? Number(prev.primaryCategoryId) : null;
      const nextPrimary =
        nextIds.length === 0
          ? null
          : currentPrimary != null && nextIds.includes(currentPrimary)
            ? currentPrimary
            : nextIds[0];

      return {
        ...prev,
        categoryIds: nextIds,
        primaryCategoryId: nextPrimary,
      };
    });
  };

  const handlePrimaryCategoryChange = (event) => {
    const nextPrimaryId = Number(event.target.value);
    if (!Number.isFinite(nextPrimaryId)) return;

    setForm((prev) => {
      const currentIds = Array.isArray(prev.categoryIds)
        ? prev.categoryIds.map(Number)
        : [];
      if (!currentIds.includes(nextPrimaryId)) return prev;

      const nextIds = [
        nextPrimaryId,
        ...currentIds.filter((id) => id !== nextPrimaryId),
      ];

      const currentCategories = Array.isArray(prev.categories) ? prev.categories : [];
      const primaryCategory = currentCategories.find(
        (category) => Number(category.id) === nextPrimaryId
      );
      const nextCategories = primaryCategory
        ? [
            primaryCategory,
            ...currentCategories.filter(
              (category) => Number(category.id) !== nextPrimaryId
            ),
          ]
        : currentCategories;

      return {
        ...prev,
        categoryIds: nextIds,
        categories: nextCategories,
        primaryCategoryId: nextPrimaryId,
      };
    });
  };

  const validateBeforeSave = () => {
    const title = String(form?.title || '');
    if (!title.trim()) {
      alert('Title is required.');
      return false;
    }
    if (title.length > NOTE_TITLE_MAX_LENGTH) {
      alert(`Title must be ${NOTE_TITLE_MAX_LENGTH} characters or fewer.`);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateBeforeSave()) return;

    const payload = {
      ...form,
      dueAt: toApiDateTime(form.dueAt),
      remindAt: toApiDateTime(form.remindAt),
      expiresAt: toApiDateTime(form.expiresAt),
      titleHtml: titleEditorHtml,
      detailHtml: editorHtml,
      detail: form.detail || '',
    };

    const saved = await saveNote?.(payload, token);
    const savedId = saved?.id || form?.id;

    if (savedId) {
      await focusNoteAfterSave?.(savedId);
    }

    setIsEditing(false);
    setIsNew(false);
  };

  const handleEdit = () => {
    setLastNoteId(form?.id);
    setTitleLimitMessage('');
    setIsEditing(true);
  };

  const handleNew = () => {
    setLastNoteId(notes[currentIndex]?.id || null);
    setForm({
      id: null,
      title: '',
      titleHtml: '',
      detail: '',
      detailHtml: '',
      dueAt: '',
      remindAt: '',
      expiresAt: '',
      sendSmsReminder: false,
      displayMode: 'normal',
      categoryIds: uncategorizedOnly ? [] : selectedCategoryIds.map(Number),
      categories: uncategorizedOnly
        ? []
        : categories.filter((category) =>
            selectedCategoryIds.some((id) => Number(id) === Number(category.id))
          ),
      primaryCategoryId:
        !uncategorizedOnly && selectedCategoryIds.length
          ? Number(selectedCategoryIds[0])
          : null,
      updatedAt: null,
      userId,
    });
    setTitleEditorHtml('');
    setEditorHtml('');
    setTitleLimitMessage('');
    setIsEditing(true);
    setIsNew(true);
  };

  const handleCancel = async () => {
    if (!form.id) {
      setActiveTab('list');
      return;
    }

    setIsEditing(false);
    setIsNew(false);
    setTitleLimitMessage('');

    const idToRestore = lastNoteId || form?.id;
    if (!idToRestore || !fetchNoteById) return;

    const note = await fetchNoteById(idToRestore, token);
    if (!note) return;

    setForm({
      id: note.id,
      title: note.title || '',
      titleHtml: note.title_html || '',
      detail: note.detail || '',
      detailHtml: note.detail_html || '',
      dueAt: note.due_at || '',
      remindAt: note.remind_at || '',
      expiresAt: note.expires_at || '',
      sendSmsReminder: !!note.send_sms_reminder,
      displayMode: note.display_mode || 'normal',
      categories: Array.isArray(note.categories) ? note.categories : [],
      categoryIds: Array.isArray(note.categories)
        ? note.categories.map((category) => Number(category.id))
        : [],
      primaryCategoryId:
        note.primary_category_id != null
          ? Number(note.primary_category_id)
          : note.primaryCategoryId != null
            ? Number(note.primaryCategoryId)
            : Array.isArray(note.categories) && note.categories.length
              ? Number(note.categories[0].id)
              : null,
      updatedAt: note.updated_at || null,
      userId: note.user_id,
    });
  };

  const handleDelete = async () => {
    if (!form?.id) return;
    const confirmed = window.confirm(
      'Are you sure you want to delete this note?'
    );
    if (!confirmed) return;

    await deleteNote?.(form.id, token);
    await fetchPage?.(notePageNo);
    setCurrentIndex(0);
    setIsEditing(false);
  };

  const readonlyDetailHtml = useMemo(() => {
    if (form.detailHtml && form.detailHtml.trim()) return sanitizeRichHtml(form.detailHtml);
    if (!form.detail) return '';
    return `<p>${String(form.detail)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>')}</p>`;
  }, [form.detail, form.detailHtml]);

  if (showNoNotesState) {
    return (
      <div className="mydata-wrapper">
        <div
          style={{
            width: '100%',
            maxWidth: 900,
            margin: '40px auto',
            background: '#fff',
            border: '1px solid #d9e0ea',
            borderRadius: 18,
            padding: '34px 32px',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 700,
              color: '#0f172a',
            }}
          >
            No notes yet
          </h2>
          <p
            style={{
              marginTop: 12,
              fontSize: 16,
              lineHeight: 1.6,
              color: '#475569',
            }}
          >
            Click below to create your first note.
          </p>

          <div className="form-buttons" style={{ marginTop: 24 }}>
            <AppButton
              variant="solid"
              size="sm"
              onPress={handleNew}
              style={{ width: 120 }}
            >
              New Note
            </AppButton>
          </div>
        </div>
      </div>
    );
  }

  const selectedCategoriesForPrimary = categories.filter((category) =>
    (form.categoryIds || []).some(
      (id) => Number(id) === Number(category.id)
    )
  );

  const effectivePrimaryCategoryId =
    form.primaryCategoryId != null &&
    selectedCategoriesForPrimary.some(
      (category) => Number(category.id) === Number(form.primaryCategoryId)
    )
      ? Number(form.primaryCategoryId)
      : selectedCategoriesForPrimary.length
        ? Number(selectedCategoriesForPrimary[0].id)
        : null;

  return (
    <div className="mydata-wrapper">
      <button
        className="nav-arrow left"
        onClick={() =>
          handlePrevResult(
            currentIndex,
            setCurrentIndex,
            listPageNo,
            setListPageNo,
            fetchPage
          )
        }
        disabled={listPageNo === 0 && currentIndex === 0}
      >
        &larr;
      </button>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="userdata-form"
      >
        {!isEditing ? (
          <>
            <header className="entry-header">
              <div className="eh-left">
                <div className="eh-eyebrow">
                  <span className="chip chip--soft">Note</span>
                  {form.displayMode === 'expanded' ? (
                    <span className="chip chip--expanded">Expanded</span>
                  ) : null}
                  {form.updatedAt ? <TimeAgo iso={form.updatedAt} /> : null}
                </div>

                <h1
                  className="eh-title"
                  dangerouslySetInnerHTML={{
                    __html: form.titleHtml && form.titleHtml.trim()
                      ? normalizeTitleHtml(form.titleHtml)
                      : escapeHtml(form.title || 'Untitled Note').replace(/\n/g, '<br>'),
                  }}
                />
              </div>
            </header>

            <div className="form-row">
              <label className="form-label">Categories</label>
              <div className="notes-category-readonly">
                {Array.isArray(form.categories) && form.categories.length ? (
                  form.categories.map((category) => {
                    const isPrimary =
                      Number(category.id) === Number(
                        form.primaryCategoryId ?? form.categories?.[0]?.id
                      );

                    return (
                      <span
                        className={`notes-category-chip ${isPrimary ? 'is-primary' : ''}`}
                        key={category.id}
                        style={getCategoryStyle(category)}
                        title={isPrimary ? `${category.name} — Primary category` : category.name}
                      >
                        {isPrimary ? (
                          <span className="notes-category-primary-mark" aria-hidden="true">★</span>
                        ) : null}
                        <span>{category.name}</span>
                        {isPrimary ? (
                          <span className="notes-category-primary-label">Primary</span>
                        ) : null}
                      </span>
                    );
                  })
                ) : (
                  <span className="notes-category-empty">Uncategorized</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">Due</label>
              <div className="readonly-value">
                {formatDisplayDateTime(form.dueAt) || '—'}
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">Reminder</label>
              <div className="readonly-value">
                {formatDisplayDateTime(form.remindAt) || '—'}
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">Expires</label>
              <div className="readonly-value">
                {formatDisplayDateTime(form.expiresAt) || '—'}
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">SMS</label>
              <div className="readonly-value">
                {form.sendSmsReminder ? 'Yes' : 'No'}
              </div>
            </div>

            <div className="detail-wrapper">
              <div className="form-row form-row-textarea">
                <div
                  className="readonly-textarea-display"
                  dangerouslySetInnerHTML={{ __html: readonlyDetailHtml }}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="form-row">
              <label className="form-label">Title</label>
              <div style={{ width: '100%', minWidth: 0 }}>
                <RichTextEditor
                  value={titleEditorHtml}
                  titleMode
                  ariaLabel="Note title"
                  maxLength={NOTE_TITLE_MAX_LENGTH}
                  onLimitReached={() =>
                    setTitleLimitMessage(
                      `Title must be ${NOTE_TITLE_MAX_LENGTH} characters or fewer.`
                    )
                  }
                  onChange={(html, plainText) => {
                    setTitleEditorHtml(html);
                    setTitleLimitMessage('');
                    setForm((prev) => ({
                      ...prev,
                      title: plainText,
                      titleHtml: html,
                    }));
                  }}
                />

                <div
                  aria-live="polite"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '6px',
                    padding: '0 4px',
                    minHeight: '18px',
                    fontSize: '0.8rem',
                  }}
                >
                  <span style={{ color: titleLimitMessage ? '#b42318' : 'transparent' }}>
                    {titleLimitMessage || ' '}
                  </span>
                  <span
                    style={{
                      flexShrink: 0,
                      color:
                        String(form.title || '').length >= NOTE_TITLE_MAX_LENGTH
                          ? '#b42318'
                          : '#667085',
                      fontWeight:
                        String(form.title || '').length >= NOTE_TITLE_MAX_LENGTH
                          ? 600
                          : 400,
                    }}
                  >
                    {String(form.title || '').length}/{NOTE_TITLE_MAX_LENGTH}
                  </span>
                </div>
              </div>
            </div>

            <div className="form-row notes-category-form-row">
              <label className="form-label notes-category-form-label">Categories</label>
              <div className="notes-category-selector">
                {categories.length ? (
                  categories.map((category) => {
                    const checked = (form.categoryIds || []).some(
                      (id) => Number(id) === Number(category.id)
                    );

                    const isPrimary =
                      checked &&
                      Number(category.id) === Number(effectivePrimaryCategoryId);

                    return (
                      <label
                        key={category.id}
                        className={`notes-category-option ${checked ? 'selected' : ''} ${
                          isPrimary ? 'is-primary' : ''
                        }`}
                        style={getCategoryStyle(category)}
                        title={isPrimary ? `${category.name} — Primary category` : category.name}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCategory(category.id)}
                        />
                        <span>{category.name}</span>
                      </label>
                    );
                  })
                ) : (
                  <span className="notes-category-empty">
                    No categories yet. Create categories from the List tab.
                  </span>
                )}
              </div>

              <div className="notes-primary-category-row">
                <div className="notes-primary-category-caption">
                  <span className="notes-primary-category-star" aria-hidden="true">★</span>
                  <span>Primary category controls note color.</span>
                </div>
                <select
                  className="notes-primary-category-select"
                  aria-label="Primary category"
                  value={effectivePrimaryCategoryId ?? ''}
                  onChange={handlePrimaryCategoryChange}
                  disabled={!selectedCategoriesForPrimary.length}
                >
                  {!selectedCategoriesForPrimary.length ? (
                    <option value="">Select a category</option>
                  ) : null}
                  {selectedCategoriesForPrimary.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">Due</label>
              <input
                type="datetime-local"
                name="dueAt"
                className="form-input"
                value={formatLocalDateTimeInput(form.dueAt)}
                onChange={handleChange}
              />
            </div>

            <div className="form-row">
              <label className="form-label">Reminder</label>
              <input
                type="datetime-local"
                name="remindAt"
                className="form-input"
                value={formatLocalDateTimeInput(form.remindAt)}
                onChange={handleChange}
              />
            </div>

            <div className="form-row">
              <label className="form-label">Expires</label>
              <input
                type="datetime-local"
                name="expiresAt"
                className="form-input"
                value={formatLocalDateTimeInput(form.expiresAt)}
                onChange={handleChange}
              />
            </div>

            <div className="form-row">
              <label className="notes-display-option">
                <input
                  type="checkbox"
                  checked={form.displayMode === 'expanded'}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      displayMode: e.target.checked ? 'expanded' : 'normal',
                    }))
                  }
                />
                <span>
                  <strong>Expanded Note</strong>
                  <small>
                    Display this note as a larger, individually positioned note
                    in the desktop Notes app.
                  </small>
                </span>
              </label>
            </div>

            <div className="form-row">
              <label className="form-label">SMS Reminder</label>
              <input
                type="checkbox"
                name="sendSmsReminder"
                checked={!!form.sendSmsReminder}
                onChange={handleChange}
              />
            </div>

            <div className="detail-wrapper">
              <div className="form-row form-row-textarea">
                <label className="form-label">Detail</label>
                <RichTextEditor
                  value={editorHtml}
                  ariaLabel="Note detail"
                  onChange={(html, plainText) => {
                    setEditorHtml(html);
                    setForm((prev) => ({
                      ...prev,
                      detail: plainText,
                      detailHtml: html,
                    }));
                  }}
                />
              </div>
            </div>
          </>
        )}

        <div className="form-buttons">
          {isEditing ? (
            <>
              <AppButton
                variant="solid"
                size="sm"
                onPress={handleSubmit}
                style={{ width: 90 }}
              >
                Save
              </AppButton>
              <AppButton
                variant="solid"
                size="sm"
                onPress={handleCancel}
                style={{ width: 90 }}
              >
                Cancel
              </AppButton>
            </>
          ) : (
            <>
              <AppButton
                variant="solid"
                size="sm"
                type="button"
                onPress={handleEdit}
                style={{ width: 90 }}
                disabled={!form?.id}
              >
                Edit
              </AppButton>
              <AppButton
                variant="solid"
                size="sm"
                onPress={handleNew}
                style={{ width: 90 }}
              >
                New
              </AppButton>
              <AppButton
                variant="solid"
                size="sm"
                onPress={handleDelete}
                style={{ width: 90 }}
                disabled={!form?.id}
              >
                Delete
              </AppButton>
            </>
          )}
        </div>
      </form>

      <button
        className="nav-arrow right"
        onClick={() =>
          handleNextResult(
            currentIndex,
            setCurrentIndex,
            notes,
            listPageNo,
            setListPageNo,
            fetchPage
          )
        }
        disabled={
          currentIndex + listPageNo * itemsPerPage >= totalNotesFound - 1
        }
      >
        &rarr;
      </button>

      <span className="entry-counter">
        {totalNotesFound > 0
          ? `${
              currentIndex + 1 + listPageNo * itemsPerPage
            } of ${totalNotesFound}`
          : '0 of 0'}
      </span>
    </div>
  );
}
